import useRouter from "@hooks/useRouter"
import useUser from "@hooks/useUser"

import { AccessCheck } from "@particles/index"

import { ListItemStack } from "@molecules"
import ListItem from "@molecules/ListItem/ListItem"
import ListItemContent from "@molecules/ListItem/ListItemContent/ListItemContent"
import ListItemDivider from "@molecules/ListItem/ListItemDivider/ListItemDivider"
import ObjectAvatar from "@molecules/ObjectAvatar/ObjectAvatar"

import { useNotificationPreferencesQuery } from "@pages/Settings/SettingsNotifications/SettingsNotifications.store"
import { useNotificationMethodsQuery } from "@pages/Settings/SettingsNotifications/components/NotificationMethodsSection/NotificationMethodsSection.store"

import { SETTINGS_ROUTES } from "@routes/settings"

function calculateTopicStatuses(topics: object, emailEnabled: boolean, smsEnabled: boolean) {
    const uniqueTopics = new Set<string>()
    const uniqueEnabledTopics = new Set<string>()

    for (const key in topics) {
        const baseKey = key.replace(/_(email|sms)$/, "")
        uniqueTopics.add(baseKey)

        // @ts-expect-error we know that the key exists
        if ((topics[`${baseKey}_email`] && emailEnabled) || (topics[`${baseKey}_sms`] && smsEnabled)) {
            uniqueEnabledTopics.add(baseKey)
        }
    }

    return { enabled: uniqueEnabledTopics.size, total: uniqueTopics.size }
}

export default function NotificationTopicsCards() {
    const { allTopics } = useNotificationPreferencesQuery()
    const { data: defaultMethodsPreference } = useNotificationMethodsQuery()
    const { user } = useUser()

    const router = useRouter()

    const emailEnabled = !!(user?.email && user?.email_verified && defaultMethodsPreference?.contact_email)
    const smsEnabled = !!(user?.phone && user?.phone_verified && defaultMethodsPreference?.contact_sms)

    const { enabled: enabledJobsAssignmentTopics, total: totalJobAssignmentTopics } = calculateTopicStatuses(
        allTopics.jobs.assignment,
        emailEnabled,
        smsEnabled,
    )
    const { enabled: enabledJobsManagerTopics, total: totalJobManagerTopics } = calculateTopicStatuses(
        allTopics.jobs.manager,
        emailEnabled,
        smsEnabled,
    )
    const { enabled: enabledEstimatesTopics, total: totalEstimatesTopics } = calculateTopicStatuses(
        allTopics.estimates,
        emailEnabled,
        smsEnabled,
    )
    const { enabled: enabledInventoryTopics, total: totalInventoryTopics } = calculateTopicStatuses(
        allTopics.inventory,
        emailEnabled,
        smsEnabled,
    )

    return (
        <ListItemStack>
            <AccessCheck entitlements={["entitlement_jobs_enabled"]}>
                <ListItem isClickable={true} onClick={() => router.push(SETTINGS_ROUTES.notificationsJobs)}>
                    <ListItem.AvatarAndContent
                        avatar={<ObjectAvatar objectType="job" size="xl" />}
                        content={
                            <ListItemContent
                                leftLabel={<ListItem.Label label="Jobs" />}
                                rightLabel={
                                    <ListItem.Label
                                        label={`${enabledJobsAssignmentTopics + enabledJobsManagerTopics} / ${totalJobAssignmentTopics + totalJobManagerTopics}`}
                                        variant="subtle"
                                    />
                                }
                            />
                        }
                    />
                    <ListItem.Actions showChevron={true} />
                </ListItem>
                <ListItemDivider />
            </AccessCheck>
            <AccessCheck entitlements={["entitlement_estimates_enabled"]}>
                <ListItem isClickable={true} onClick={() => router.push(SETTINGS_ROUTES.notificationsEstimates)}>
                    <ListItem.AvatarAndContent
                        avatar={<ObjectAvatar objectType="estimate" size="xl" />}
                        content={
                            <ListItemContent
                                leftLabel={<ListItem.Label label="Estimates" />}
                                rightLabel={
                                    <ListItem.Label
                                        label={`${enabledEstimatesTopics} / ${totalEstimatesTopics}`}
                                        variant="subtle"
                                    />
                                }
                            />
                        }
                    />
                    <ListItem.Actions showChevron={true} />
                </ListItem>
                <ListItemDivider />
            </AccessCheck>

            <ListItem isClickable={true} onClick={() => router.push(SETTINGS_ROUTES.notificationsInventory)}>
                <ListItem.AvatarAndContent
                    avatar={<ObjectAvatar objectType="pricebook_item" size="xl" />}
                    content={
                        <ListItemContent
                            leftLabel={<ListItem.Label label="Inventory" />}
                            rightLabel={
                                <ListItem.Label
                                    label={`${enabledInventoryTopics} / ${totalInventoryTopics}`}
                                    variant="subtle"
                                />
                            }
                        />
                    }
                />
                <ListItem.Actions showChevron={true} />
            </ListItem>
        </ListItemStack>
    )
}
